import React from 'react'
import { Tooltip } from './Tooltip'

export const IconButton = React.forwardRef<
  HTMLButtonElement | HTMLDivElement,
  {
    icon: React.ReactNode
    screenReaderText: string
    tooltip?: string
    tooltipPosition?: 'top-center' | 'top-right' | 'bottom-center'
    variant?: 'outline' | 'transparent' | 'just-icon' | 'outline-circle'
    size?: 'sm' | 'md' | 'lg'
    trigger?: boolean
    disabled?: boolean
    onClick?: () => void
    dataRole?: string
  }
>(
  (
    {
      icon,
      screenReaderText,
      tooltip,
      tooltipPosition = 'top-center',
      onClick,
      variant = 'transparent',
      size = 'md',
      trigger = false,
      disabled = false,
      dataRole = 'icon-button',
    },
    ref
  ) => {
    const borderClasses =
      variant === 'outline' ? 'border border-primary' : variant === 'outline-circle' ? 'border-2 border-primary' : ''

    const paddingClasses = variant === 'just-icon' ? '' : variant === 'outline-circle' ? 'p-0' : 'p-2'

    const interactionClasses =
      variant === 'just-icon'
        ? ''
        : "hover:bg-secondary hover:text-primary active:bg-tertiary group-[[data-state='open']]/trigger:bg-tertiary"

    const Element = trigger ? 'div' : 'button'
    const sizeClass = size === 'sm' ? 'icon-button-w-4-h-4' : 'icon-button-w-5-h-5'
    const roundingClass = variant === 'outline-circle' ? 'rounded-full' : 'rounded-lg'

    const buttonContent = (
      <Element
        ref={ref as any}
        type={trigger ? undefined : 'button'}
        onClick={onClick}
        disabled={disabled}
        data-role={dataRole}
        className={`
        icon-button ${sizeClass}
        relative flex flex-col items-center
        icon-primary ${roundingClass} ${paddingClasses}
        ${interactionClasses}
        ${borderClasses}
      `}
      >
        <span className="sr-only">{screenReaderText}</span>
        {icon}
      </Element>
    )

    if (tooltip) {
      return (
        <Tooltip tooltip={tooltip} position={tooltipPosition}>
          {buttonContent}
        </Tooltip>
      )
    }

    return buttonContent
  }
)

IconButton.displayName = 'IconButton'
